import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { StringFormatter } from '@repo/toolkit/string'
import Badge from '@repo/ui/badge'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getForeignStaffTravelSummary } from '../../../api/affected-staffs/get-foreign-staff-travel-summary'
import { TravelChoiceEnum } from '../../../lib/types/enums/affected-staff/travel-choice.enum'
import { AffectedStaff } from '../../../lib/types/models/affectedStaff/affected-staff'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'
import TransportPicto from '../../elements/transport-picto'
import LabelWithText from '../ols/label-with-text'
import { convertTimeZone } from '../trip-folders/trip-folder-reservation/trip-folder-reservation-details'

type Props = {
  affectedStaff: AffectedStaff
}

// Read-only mirror of the staff personal space "Round trip journeys" tab, driven by the
// SAVED travel choice so the admin sees exactly what the English staff sees.
const ForeignStaffTravelSummary = ({ affectedStaff }: Props) => {
  const { data: summary } = useQuery({
    // The saved travel choice is in the key: saving the form only refetches the affected staff
    queryKey: [
      'affected-staff',
      affectedStaff.id,
      'foreign-travel-summary',
      affectedStaff.travelChoice
    ],
    queryFn: () => getForeignStaffTravelSummary(affectedStaff.id)
  })
  const travelChoice = summary?.travelChoice

  if (!travelChoice) {
    return (
      <Typography variant="tag" color={tailwindColors.grey.lighter}>
        Choix d&apos;arrivée non renseigné
      </Typography>
    )
  }

  if (travelChoice === TravelChoiceEnum.INDIVIDUAL) {
    const individual = summary.individual
    return (
      <div>
        <Badge text="Aller" variant="warning" />
        <div className="h-2" />
        <div className="grid grid-cols-3 items-center">
          <LabelWithText
            label="Adresse d'arrivée"
            data={valueRender(individual?.centerAddress ?? undefined)}
          />
          {/* this is static always 16:00 */}
          <LabelWithText
            label="Heure d'arrivée"
            data={<Badge text={valueRender(individual?.arrivalTime)} />}
          />
          <LabelWithText
            label="Responsable du centre"
            data={valueRender(
              individual?.centerManager ?? undefined,
              ({ firstName, lastName, phoneNumber }) =>
                `${StringFormatter.capitalize(firstName)} ${lastName.toUpperCase()}${phoneNumber ? ` - ${formatPhoneNumber(phoneNumber)} (Numéro Etranger)` : ''}`
            )}
          />
        </div>

        <div className="h-3" />

        <Badge text="Retour" variant="success" />
        <div className="h-2" />
        <Typography variant="tag">Départ possible du collège à partir de 11h</Typography>
      </div>
    )
  }

  const inbound = summary.group.inbound
  const outbound = summary.group.outbound
  const tripPicto = inbound?.transportMeans?.picto

  return (
    <div>
      <Badge text="Aller" variant="warning" />
      <div className="h-2" />
      <div className="grid grid-cols-3 items-center">
        <LabelWithText
          label="Voyage"
          data={
            <div className="flex items-center gap-2">
              {tripPicto && <TransportPicto picto={tripPicto} className="bg-primary-light" />}
              <Typography variant="tag">
                {TripProperties.getTripLabel(inbound?.trip ?? undefined)}
              </Typography>
            </div>
          }
        />
        <LabelWithText
          label="Heure de RDV"
          data={
            <Badge
              text={valueRender(inbound?.meetingTime ?? undefined, (time) =>
                convertTimeZone(time, 'Local', inbound?.meetingTimeZone ?? undefined)
              )}
            />
          }
        />
        <LabelWithText label="Lieu de RDV" data={valueRender(inbound?.meetingPlace)} />
      </div>

      <div className="h-3" />

      <Badge text="Retour" variant="success" />
      <div className="h-2" />
      <div className="grid grid-cols-3 items-center">
        <LabelWithText
          label="Drop-off time"
          data={
            <Badge
              text={valueRender(outbound?.arrivalTime ?? undefined, (time) =>
                convertTimeZone(time, 'Local', outbound?.arrivalTimeZone ?? undefined)
              )}
            />
          }
        />
        <LabelWithText label="Drop-off place" data={valueRender(outbound?.arrivalPlace)} />
      </div>
    </div>
  )
}

export default ForeignStaffTravelSummary
